import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Divider, Grid, InputAdornment, Tooltip as MuiTooltip } from '@mui/material';
import { Fragment } from 'react';
import {
  ArrayInput,
  AutocompleteInput,
  BooleanInput,
  Create,
  DateInput,
  FileField,
  FileInput,
  FormDataConsumer,
  FormTab,
  ImageField,
  ImageInput,
  Labeled,
  NumberInput,
  PasswordInput,
  ReferenceArrayInput,
  ReferenceInput,
  SelectArrayInput,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextInput,
  TimeInput,
  email,
  maxLength,
  usePermissions,
  useTranslate
} from 'react-admin';
import { CheckChainUsesModule, ModuleKey, Permission, PriceInput, Tooltip } from '../../components';
import { CEPInput, CNPJInput, PhoneInput, RtiTokenInput, StateInput } from '../../components/Inputs';
import Permissions from '../../components/Permissions';
import { CnpjLookupButton } from './CnpjLookupButton';
import { EvoCnpjCheck, validateEvoCnpjCheck } from './EvoCnpjCheck';
import { PERMISSIONS, daysOfTheWeek, minimumValueCashbackToolTip } from '../../constants';
import { filterToQuery } from '../../utils';
import { ConcentratorBrandType, IntegrationType, TagType } from '../../models';
import { SislavFields, clearSislavFields } from '../../components/SislavFields';
import { FinancesStation } from './EditStation';
import { filterConcentrators, filterNumericIds, normalizeCredentials, requiredWithCredentials, validateCredentialHost } from '../../lib/stationPartnerForm';
import { __t } from '../../i18n/t';

const choices = [
  { id: 'company-tec', name: 'CompanyTec' },
  { id: 'ez-tec', name: 'EZTec' },
  { id: 'auto-system', name: 'AutoSystem' },
];

const required = (message = 'Required') => value =>
  value ? undefined : message;

const createStationEmailValidate = [email(), required()];
const validateCredentialsTogether = [requiredWithCredentials];
const validateCredentialsHost = [requiredWithCredentials, validateCredentialHost];

const transformStation = (data) => {
  if (data.integration !== IntegrationType.rti) {
    data.rtiToken = null;
  }
  clearSislavFields(data);
  // Estado da validação do CNPJ no EVO: vive só na tela, o backend não conhece essa chave.
  delete data.evoCheckStatus;
  // Credencial só vale para auto-system, e toda vazia é "não informada" ({"port":null} dava 400).
  normalizeCredentials(data, data.syncType === 'auto-system');
  return filterNumericIds(filterConcentrators(data));
}

export const CreateStation = ({ ...props }) => {
  const { permissions } = usePermissions();
  const translate = useTranslate();

  return (
    <Create title="Novo Posto" {...props} transform={transformStation}>
      <TabbedForm sanitizeEmptyValues>
        <FormTab label="Identificação">
          <ArrayInput source="numericIds" label="IDs Numéricos">
            <SimpleFormIterator
              disableReordering
              getItemLabel={() => null}

            >
              <NumberInput label={false} />
            </SimpleFormIterator>
          </ArrayInput>
          <Grid container spacing={2} sx={{ maxWidth: 800 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextInput source="name" label="Nome" validate={required()} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CNPJInput source="cnpj" label="CNPJ" validate={required()} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <CnpjLookupButton />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextInput
                source="email"
                label="Email"
                type="email"
                validate={createStationEmailValidate}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <PhoneInput source="phone" label="Telefone" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextInput source="corporateName" label="Razão Social" validate={[maxLength(120)]} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextInput source="stateRegistration" label="Incrição Estadual" validate={[maxLength(12)]} fullWidth />
            </Grid>
            {permissions && permissions.includes(PERMISSIONS.ADMIN) && (
              <Grid size={{ xs: 12, md: 10 }}>
                <ReferenceInput
                  source="chainId"
                  reference="chains"
                  perPage={null}
                  sort={{ field: 'name', order: 'ASC' }}
                >
                  <AutocompleteInput
                    label="Rede"
                    optionText="name"
                    filterToQuery={filterToQuery}
                    validate={required()}
                    fullWidth
                  />
                </ReferenceInput>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 5 }}>
              <ReferenceArrayInput
                sort={{ field: 'name', order: 'ASC' }}
                perPage={null}
                source="fuelIds"
                reference="fuels"
              >
                <SelectArrayInput label="Combustível" optionText="name" fullWidth />
              </ReferenceArrayInput>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <ReferenceArrayInput
                sort={{ field: 'name', order: 'ASC' }}
                perPage={null}
                source="serviceIds"
                reference="services"
              >
                <SelectArrayInput label="Serviços" optionText="name" fullWidth />
              </ReferenceArrayInput>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <FormDataConsumer>
                {({ formData }) => (
                  <ReferenceArrayInput
                    sort={{ field: 'name', order: 'ASC' }}
                    filter={{ type: TagType.station, chainId: formData.chainId }}
                    perPage={null}
                    source="tagIds"
                    reference="chains/chainId/tag"
                  >
                    <SelectArrayInput label="Etiquetas" optionText="name" fullWidth />
                  </ReferenceArrayInput>
                )}
              </FormDataConsumer>
            </Grid>
            {permissions && permissions.includes(PERMISSIONS.ADMIN) && (
              <Grid size={{ xs: 12, md: 5 }}>
                <ReferenceArrayInput
                  sort={{ field: 'name', order: 'ASC' }}
                  perPage={null}
                  source="appPaymentMethodIds"
                  reference="app-payment-methods"
                >
                  <SelectArrayInput label="Método de pagamento" optionText="name" fullWidth />
                </ReferenceArrayInput>
              </Grid>
            )}
            {/* Mesma correção do EditStation: `size={12}` para o container aninhado ocupar a linha.
                O `p: 2` virou `py: 2` — o recuo lateral desalinhava esta linha das outras do form. */}
            <Grid container size={12} spacing={2} sx={{ py: 2 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <SelectInput
                  label="Integração"
                  emptyText="ra.action.clear_input_value"
                  source="integration"
                  choices={IntegrationType.integrationChoices}
                  validate={validateEvoCnpjCheck}
                  fullWidth
                />
              </Grid>
              <EvoCnpjCheck size={{ xs: 12, md: 8 }} />
              <FormDataConsumer>
                {({ formData }) => (
                  formData.integration === IntegrationType.rti &&
                  <Grid size={{ xs: 12, md: 4 }}>
                    <RtiTokenInput
                      label="Token RTI"
                      source="rtiToken"
                      fullWidth
                    />
                  </Grid>
                )}
              </FormDataConsumer>
              <FormDataConsumer>
                {({ formData }) => (
                  formData.integration === IntegrationType.trevo &&
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextInput
                      label="Token Top Banho (Rede Trevo)"
                      source="trevoToken"
                      fullWidth
                    />
                  </Grid>
                )}
              </FormDataConsumer>
              <SislavFields />
            </Grid>
            <Permissions permissions={[PERMISSIONS.ADMIN]}>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextInput
                  label="Token Fixpay"
                  source="fixpayToken"
                  fullWidth
                />
              </Grid>
            </Permissions>
          </Grid>
          <Permissions permissions={[PERMISSIONS.ADMIN]}>
            <BooleanInput
              label="Preset por Valor (Encher tanque)"
              source="presetUsingValue"
              defaultValue={false}
            />
          </Permissions>
          <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <BooleanInput label="Aceita cashback?" source="acceptsCashback" defaultValue={false} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Tooltip title={minimumValueCashbackToolTip} placement="top-start" sxContainer={{ display: "-webkit-box" }}>
                <PriceInput source="minimumValueToRedeemCashback" label="Mínimo para resgate de cashback" sx={{ minWidth: 220 }} />
              </Tooltip>
            </Grid>
          </Grid>
          <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.generateCashbackOnFillin')}>
            <BooleanInput label="Gerar cashback no abastecimento" source="generateCashbackOnFillin" defaultValue />
          </Tooltip>
          <BooleanInput label="Usar níveis de preço" source="usePriceTiers" defaultValue={false} />
          <FormDataConsumer>
            {({ formData }) => formData.usePriceTiers && (
              <ArrayInput source="priceTierLabels" label="Rótulos dos níveis de preço" helperText="Ex: índice 1 = Dinheiro, índice 2 = Crédito">
                <SimpleFormIterator disableReordering inline>
                  <NumberInput source="index" label="Índice (1, 2, 3...)" min={1} style={{ width: 160 }} />
                  <TextInput source="label" label="Rótulo" style={{ width: 180 }} />
                  <TextInput
                    source="icon"
                    label="Ícone"
                    style={{ width: 200 }}
                    slotProps={{ input: { endAdornment: (
                      <InputAdornment position="end">
                        <MuiTooltip title="Nome do ícone da biblioteca MaterialCommunityIcons. Acesse materialdesignicons.com para pesquisar. Ex: credit-card, cash, bank" placement="top">
                          <HelpOutlineIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                        </MuiTooltip>
                      </InputAdornment>
                    ) } }}
                  />
                  <BooleanInput source="applyDiscount" label="Aplicar desconto" defaultValue={true} helperText="Desative para não aplicar descontos neste nível de preço" />
                </SimpleFormIterator>
              </ArrayInput>
            )}
          </FormDataConsumer>
          <BooleanInput label="Usar integração com Software House" source="useIntegrationSoftwareHouse" defaultValue={false} />
          <BooleanInput label="Mostrar preço durante o abastecimento" source="useFillinFollowUp" defaultValue />
          <BooleanInput label="Acompanhar Abastecimento GNV em tempo real" source="useFillinFollowUpGnv" defaultValue />
          <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.pointsWhenDiscounting')}>
            <BooleanInput label="Pontuar quando usar desconto" source="pointsWhenDiscounting" defaultValue />
          </Tooltip>
          <ImageInput source="imagefile" label="Imagem" accept={{ 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] }}>
            <ImageField source="src" label="Imagem" />
          </ImageInput>
        </FormTab>
        <Permission permission={PERMISSIONS.ADMIN}>
          <FormTab label="Concetradores">
            <ArrayInput source="concentrators" label="IDs Numéricos">
              <SimpleFormIterator
                disableReordering
                getItemLabel={() => null}
              >
                <NumberInput source="numericId" label="Id numérico" validate={required(__t('Informe o id numérico'))} style={{ width: '260px' }} />
                <SelectInput
                  validate={[required()]}
                  label="Sistema"
                  source="brandType"
                  choices={ConcentratorBrandType.concentratorBrandChoices}
                  style={{ width: '260px' }}
                />
              </SimpleFormIterator>
            </ArrayInput>
          </FormTab>
        </Permission>
        <FormTab label="Endereço">
          <Grid container spacing={2} sx={{ maxWidth: 800 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextInput source="street" label="Rua" validate={required()} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextInput source="streetNumber" label="Número" validate={required()} fullWidth />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ maxWidth: 800 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <CEPInput source="cep" label="CEP" validate={required()} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextInput source="neighbourhood" label="Bairro" validate={required()} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextInput source="city" label="Cidade" validate={required()} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <StateInput source="state" label="Estado" validate={required()} fullWidth />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ maxWidth: 800 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <NumberInput source="latitude" label="Latitude" validate={required()} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <NumberInput source="longitude" label="Longitude" validate={required()} fullWidth />
            </Grid>
          </Grid>
        </FormTab>
        {permissions && permissions.includes(PERMISSIONS.ADMIN) && (
          <FormTab label='GPS'>
            <Labeled fullWidth label="Informe as coordenadas em 4 pontos que formam um retângulo" sx={{ margin: '10px 0', textAlign: 'center', fontWeight: 'bold' }}>
              <FormDataConsumer>
                {({ formData: { coordinates } }) =>
                  <ArrayInput source='coordinates' label=''>
                    <SimpleFormIterator disableReordering disableAdd={coordinates && coordinates.length === 4}>
                      <NumberInput source="latitude" label="Latitude" validate={required()} />
                      <NumberInput source="longitude" label="Longitude" validate={required()} />
                    </SimpleFormIterator>
                  </ArrayInput>
                }
              </FormDataConsumer>
            </Labeled>
          </FormTab>)}
        {permissions && permissions.includes(PERMISSIONS.ADMIN) && (
          <FormTab label="Sincronização">
            <SelectInput
              label="Sistema"
              source="syncType"
              choices={choices}
              validate={[required()]}
              style={{ width: '260px' }}
            />
            <FormDataConsumer>
              {({ formData: { syncType } }) =>
                syncType === 'auto-system' && (
                  <>
                    <TextInput
                      source="credentials.host"
                      label="Host ou ip"
                      validate={validateCredentialsHost}
                      InputProps={{ autoComplete: 'off' }}
                      sx={{ display: 'block' }}
                    />
                    <NumberInput
                      source="credentials.port"
                      label="Porta"
                      validate={validateCredentialsTogether}
                      InputProps={{ autoComplete: 'off' }}
                      sx={{ display: 'block' }}
                    />
                    <TextInput
                      source="credentials.user"
                      label="Usuário"
                      validate={validateCredentialsTogether}
                      InputProps={{ autoComplete: 'nope' }}
                      sx={{ display: 'block' }}
                    />
                    <TextInput
                      source="credentials.password"
                      label="Senha"
                      validate={validateCredentialsTogether}
                      type="password"
                      InputProps={{ autoComplete: 'new-password' }}
                      sx={{ display: 'block' }}
                    />
                    <TextInput
                      source="credentials.database"
                      label="Banco"
                      validate={validateCredentialsTogether}
                      InputProps={{ autoComplete: 'off' }}
                      sx={{ display: 'block' }}
                    />
                  </>
                )
              }
            </FormDataConsumer>
          </FormTab>)}
        <FormTab label="Horários">
          <ArrayInput source="openingHours" label={false} defaultValue={daysOfTheWeek.map(wd => ({ closed: true, dayOfTheWeek: wd.id }))}>
            <SimpleFormIterator disableAdd disableRemove disableReordering >
              <FormDataConsumer>
                {({ scopedFormData }) => {
                  return (
                    <p style={{ fontSize: 16, margin: '20px 0' }}>{scopedFormData && daysOfTheWeek.find(wd => wd.id === scopedFormData.dayOfTheWeek).name}</p>
                  );
                }}
              </FormDataConsumer>
              <FormDataConsumer>
                {({ scopedFormData }) => {
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', margin: '0 0 20px 0' }}>
                      <BooleanInput
                        source="closed"
                        label='Fechado'
                        sx={{ mr: 2.5 }}
                      />
                      {scopedFormData && !scopedFormData.closed &&
                        <BooleanInput
                          source="open24hrs"
                          label='Aberto 24 horas'
                          sx={{ mr: 2.5 }}
                        />
                      }
                      {scopedFormData && !scopedFormData.closed && !scopedFormData.open24hrs &&
                        <Fragment>
                          <TimeInput
                            record={scopedFormData}
                            variant='filled'
                            label='Hora de abertura'
                            source="startTime"
                            style={{ margin: '0 20px 0 0' }}
                            fullDate
                          />
                          <TimeInput
                            record={scopedFormData}
                            variant='filled'
                            label='Hora de fechamento'
                            source="endTime"
                            fullDate
                          />
                        </Fragment>
                      }
                    </Box>
                  );
                }}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
        </FormTab>
        <FormTab label="Feriados">
          <ArrayInput source="stationHolidays" label="">
            <SimpleFormIterator disableReordering>
              <DateInput source="date" label="Data" />
            </SimpleFormIterator>
          </ArrayInput>
        </FormTab>
        <FormTab label="Financeiro">
          <FinancesStation />
        </FormTab>
        <CheckChainUsesModule module={ModuleKey.DOTZ}>
          <FormTab label="Dotz">
            <TextInput source="dotzUser" label="Usuário" sx={{ minWidth: 300 }} />
            <PasswordInput source="dotzPassword" label="Senha" inputProps={{ autocomplete: 'current-password' }} sx={{ minWidth: 300 }} />
            <PasswordInput source="dotzCertPassphrase" label="Senha do certificado" inputProps={{ autocomplete: 'current-password' }} sx={{ minWidth: 300 }} />
            <FileInput source="dotzCertificate" label="Certificado Dotz" accept={{ 'application/x-pkcs12': ['.pfx'] }}>
              <FileField source="src" title="title" />
            </FileInput>
          </FormTab>
        </CheckChainUsesModule>
        <Permission permission={PERMISSIONS.ADMIN}>
          <FormTab label="Integrações">
            <Grid container spacing={2} sx={{ maxWidth: 800 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormDataConsumer>
                  {({ formData }) => {
                    return (
                      <>
                        <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.useSanvitronIntegration')}>
                          <BooleanInput
                            label="Integrar com Sanvitron"
                            source="useSanvitronIntegration"
                          />
                        </Tooltip>
                        {formData.useSanvitronIntegration && (
                          <>
                            <TextInput
                              source="sanvitronLogin"
                              label="Usuário Sanvitron"
                              fullWidth
                            />
                            <PasswordInput
                              source="sanvitronPassword"
                              label="Senha Sanvitron"
                              fullWidth
                            />
                            <TextInput
                              source="sanvitronBaseUrl"
                              label="URL Sanvitron"
                              fullWidth
                            />
                          </>
                        )}
                      </>
                    );
                  }}
                </FormDataConsumer>
                <Divider fullWidth />
              </Grid>
            </Grid>
          </FormTab>
        </Permission>
      </TabbedForm>
    </Create>
  );
}

export default CreateStation;
